/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  AdditionalInformationUpdateRequest,
  InvestmentDetails,
  InvestmentWalletAssociationRequest,
  InvestmentWalletSelectionRequest,
  KycInformation,
  PaginatedDropdownElementList,
  PaginatedInvestmentDetailsList,
} from '../models/index';
import {
    AdditionalInformationUpdateRequestFromJSON,
    AdditionalInformationUpdateRequestToJSON,
    InvestmentDetailsFromJSON,
    InvestmentDetailsToJSON,
    InvestmentWalletAssociationRequestFromJSON,
    InvestmentWalletAssociationRequestToJSON,
    InvestmentWalletSelectionRequestFromJSON,
    InvestmentWalletSelectionRequestToJSON,
    KycInformationFromJSON,
    KycInformationToJSON,
    PaginatedDropdownElementListFromJSON,
    PaginatedDropdownElementListToJSON,
    PaginatedInvestmentDetailsListFromJSON,
    PaginatedInvestmentDetailsListToJSON,
} from '../models/index';

export interface InvestmentsAbortionCreateRequest {
    id: string;
}

export interface InvestmentsAdditionalInformationCreateRequest {
    id: string;
    additionalInformationUpdateRequest?: AdditionalInformationUpdateRequest;
}

export interface InvestmentsBenefitingPersonConfirmationCreateRequest {
    id: string;
}

export interface InvestmentsDropdownListRequest {
    acceptedAtAfter?: Date;
    acceptedAtBefore?: Date;
    acquisitionType?: InvestmentsDropdownListAcquisitionTypeEnum;
    addWalletLater?: InvestmentsDropdownListAddWalletLaterEnum;
    campaign?: string;
    cancellationDateAfter?: Date;
    cancellationDateBefore?: Date;
    cancellationReason?: InvestmentsDropdownListCancellationReasonEnum;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    distributionPlatformDisplayName?: string;
    distributionPlatformId?: string;
    exemptionOrderDateAfter?: Date;
    exemptionOrderDateBefore?: Date;
    field?: InvestmentsDropdownListFieldEnum;
    frontendStatus?: InvestmentsDropdownListFrontendStatusEnum;
    fulfilledAmountMax?: string;
    fulfilledAmountMin?: string;
    id?: string;
    initiatedIdentification?: boolean;
    investmentTotalFulfilledMax?: number;
    investmentTotalFulfilledMin?: number;
    investmentTotalMax?: number;
    investmentTotalMin?: number;
    investorId?: string;
    investorName?: string;
    lastSettledPayoutAmountMax?: number;
    lastSettledPayoutAmountMin?: number;
    limit?: number;
    maxAmountMax?: number;
    maxAmountMin?: number;
    nextScheduledPayoutAfter?: Date;
    nextScheduledPayoutBefore?: Date;
    offset?: number;
    ordering?: InvestmentsDropdownListOrderingEnum;
    paidAtAfter?: Date;
    paidAtBefore?: Date;
    paymentCode?: string;
    paymentDeadlineAfter?: Date;
    paymentDeadlineBefore?: Date;
    paymentTotalMax?: number;
    paymentTotalMin?: number;
    productId?: string;
    productName?: string;
    remainingAmountMax?: number;
    remainingAmountMin?: number;
    search?: string;
    signedAtAfter?: Date;
    signedAtBefore?: Date;
    status?: InvestmentsDropdownListStatusEnum;
    step?: InvestmentsDropdownListStepEnum;
    sumSettledPayoutsMax?: number;
    sumSettledPayoutsMin?: number;
    surveyValid?: boolean;
    surveyWaiverType?: InvestmentsDropdownListSurveyWaiverTypeEnum;
    tokenDeliveredAtAfter?: Date;
    tokenDeliveredAtBefore?: Date;
    tokenTransactionHash?: string;
    voucherId?: string;
}

export interface InvestmentsIdentificationRetrieveRequest {
    id: string;
}

export interface InvestmentsIdentificationStartNotificationCreateRequest {
    id: string;
}

export interface InvestmentsListRequest {
    acceptedAtAfter?: Date;
    acceptedAtBefore?: Date;
    acquisitionType?: Array<InvestmentsListAcquisitionTypeEnum>;
    addWalletLater?: Array<InvestmentsListAddWalletLaterEnum | null>;
    campaign?: Array<string>;
    cancellationDateAfter?: Date;
    cancellationDateBefore?: Date;
    cancellationReason?: Array<InvestmentsListCancellationReasonEnum | null>;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    distributionPlatformDisplayName?: Array<string>;
    distributionPlatformId?: Array<string>;
    exemptionOrderDateAfter?: Date;
    exemptionOrderDateBefore?: Date;
    frontendStatus?: InvestmentsListFrontendStatusEnum;
    fulfilledAmountMax?: string;
    fulfilledAmountMin?: string;
    id?: Array<string>;
    initiatedIdentification?: boolean;
    investmentTotalFulfilledMax?: number;
    investmentTotalFulfilledMin?: number;
    investmentTotalMax?: number;
    investmentTotalMin?: number;
    investorId?: Array<string>;
    investorName?: Array<string>;
    lastSettledPayoutAmountMax?: number;
    lastSettledPayoutAmountMin?: number;
    limit?: number;
    maxAmountMax?: number;
    maxAmountMin?: number;
    nextScheduledPayoutAfter?: Date;
    nextScheduledPayoutBefore?: Date;
    offset?: number;
    ordering?: InvestmentsListOrderingEnum;
    paidAtAfter?: Date;
    paidAtBefore?: Date;
    paymentCode?: Array<string>;
    paymentDeadlineAfter?: Date;
    paymentDeadlineBefore?: Date;
    paymentTotalMax?: number;
    paymentTotalMin?: number;
    productId?: Array<string>;
    productName?: Array<string>;
    remainingAmountMax?: number;
    remainingAmountMin?: number;
    search?: string;
    signedAtAfter?: Date;
    signedAtBefore?: Date;
    status?: InvestmentsListStatusEnum;
    step?: InvestmentsListStepEnum;
    sumSettledPayoutsMax?: number;
    sumSettledPayoutsMin?: number;
    surveyValid?: boolean;
    surveyWaiverType?: Array<InvestmentsListSurveyWaiverTypeEnum>;
    tokenDeliveredAtAfter?: Date;
    tokenDeliveredAtBefore?: Date;
    tokenTransactionHash?: string;
    voucherId?: Array<string>;
}

export interface InvestmentsResetWalletSelectionCreateRequest {
    id: string;
}

export interface InvestmentsRetrieveRequest {
    id: string;
}

export interface InvestmentsValuesRetrieveRequest {
    field: InvestmentsValuesRetrieveFieldEnum;
    acceptedAtAfter?: Date;
    acceptedAtBefore?: Date;
    acquisitionType?: InvestmentsValuesRetrieveAcquisitionTypeEnum;
    addWalletLater?: InvestmentsValuesRetrieveAddWalletLaterEnum;
    campaign?: string;
    cancellationDateAfter?: Date;
    cancellationDateBefore?: Date;
    cancellationReason?: InvestmentsValuesRetrieveCancellationReasonEnum;
    createdAtAfter?: Date;
    createdAtBefore?: Date;
    distributionPlatformDisplayName?: string;
    distributionPlatformId?: string;
    exemptionOrderDateAfter?: Date;
    exemptionOrderDateBefore?: Date;
    frontendStatus?: InvestmentsValuesRetrieveFrontendStatusEnum;
    fulfilledAmountMax?: string;
    fulfilledAmountMin?: string;
    icontains?: string;
    id?: string;
    initiatedIdentification?: boolean;
    investmentTotalFulfilledMax?: number;
    investmentTotalFulfilledMin?: number;
    investmentTotalMax?: number;
    investmentTotalMin?: number;
    investorId?: string;
    investorName?: string;
    lastSettledPayoutAmountMax?: number;
    lastSettledPayoutAmountMin?: number;
    maxAmountMax?: number;
    maxAmountMin?: number;
    nextScheduledPayoutAfter?: Date;
    nextScheduledPayoutBefore?: Date;
    ordering?: InvestmentsValuesRetrieveOrderingEnum;
    paidAtAfter?: Date;
    paidAtBefore?: Date;
    paymentCode?: string;
    paymentDeadlineAfter?: Date;
    paymentDeadlineBefore?: Date;
    paymentTotalMax?: number;
    paymentTotalMin?: number;
    productId?: string;
    productName?: string;
    remainingAmountMax?: number;
    remainingAmountMin?: number;
    search?: string;
    signedAtAfter?: Date;
    signedAtBefore?: Date;
    status?: InvestmentsValuesRetrieveStatusEnum;
    step?: InvestmentsValuesRetrieveStepEnum;
    sumSettledPayoutsMax?: number;
    sumSettledPayoutsMin?: number;
    surveyValid?: boolean;
    surveyWaiverType?: InvestmentsValuesRetrieveSurveyWaiverTypeEnum;
    tokenDeliveredAtAfter?: Date;
    tokenDeliveredAtBefore?: Date;
    tokenTransactionHash?: string;
    voucherId?: string;
}

export interface InvestmentsWalletAssociationCreateRequest {
    id: string;
    investmentWalletAssociationRequest: InvestmentWalletAssociationRequest;
}

export interface InvestmentsWalletSelectionCreateRequest {
    id: string;
    investmentWalletSelectionRequest: InvestmentWalletSelectionRequest;
}

/**
 * 
 */
export class InvestmentsApi extends runtime.BaseAPI {

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsAbortionCreateRaw(requestParameters: InvestmentsAbortionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsAbortionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/{id}/abortion/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsAbortionCreate(requestParameters: InvestmentsAbortionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsAbortionCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsAdditionalInformationCreateRaw(requestParameters: InvestmentsAdditionalInformationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsAdditionalInformationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/investments/{id}/additional-information/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: AdditionalInformationUpdateRequestToJSON(requestParameters['additionalInformationUpdateRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsAdditionalInformationCreate(requestParameters: InvestmentsAdditionalInformationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsAdditionalInformationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsBenefitingPersonConfirmationCreateRaw(requestParameters: InvestmentsBenefitingPersonConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsBenefitingPersonConfirmationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/{id}/benefiting-person-confirmation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsBenefitingPersonConfirmationCreate(requestParameters: InvestmentsBenefitingPersonConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsBenefitingPersonConfirmationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async investmentsDropdownListRaw(requestParameters: InvestmentsDropdownListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedDropdownElementList>> {
        const queryParameters: any = {};

        if (requestParameters['acceptedAtAfter'] != null) {
            queryParameters['accepted_at_after'] = (requestParameters['acceptedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['acceptedAtBefore'] != null) {
            queryParameters['accepted_at_before'] = (requestParameters['acceptedAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['acquisitionType'] != null) {
            queryParameters['acquisition_type'] = requestParameters['acquisitionType'];
        }

        if (requestParameters['addWalletLater'] != null) {
            queryParameters['add_wallet_later'] = requestParameters['addWalletLater'];
        }

        if (requestParameters['campaign'] != null) {
            queryParameters['campaign'] = requestParameters['campaign'];
        }

        if (requestParameters['cancellationDateAfter'] != null) {
            queryParameters['cancellation_date_after'] = (requestParameters['cancellationDateAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['cancellationDateBefore'] != null) {
            queryParameters['cancellation_date_before'] = (requestParameters['cancellationDateBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['cancellationReason'] != null) {
            queryParameters['cancellation_reason'] = requestParameters['cancellationReason'];
        }

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['distributionPlatformDisplayName'] != null) {
            queryParameters['distribution_platform_display_name'] = requestParameters['distributionPlatformDisplayName'];
        }

        if (requestParameters['distributionPlatformId'] != null) {
            queryParameters['distribution_platform_id'] = requestParameters['distributionPlatformId'];
        }

        if (requestParameters['exemptionOrderDateAfter'] != null) {
            queryParameters['exemption_order_date_after'] = (requestParameters['exemptionOrderDateAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['exemptionOrderDateBefore'] != null) {
            queryParameters['exemption_order_date_before'] = (requestParameters['exemptionOrderDateBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['frontendStatus'] != null) {
            queryParameters['frontend_status'] = requestParameters['frontendStatus'];
        }

        if (requestParameters['fulfilledAmountMax'] != null) {
            queryParameters['fulfilled_amount_max'] = requestParameters['fulfilledAmountMax'];
        }

        if (requestParameters['fulfilledAmountMin'] != null) {
            queryParameters['fulfilled_amount_min'] = requestParameters['fulfilledAmountMin'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['initiatedIdentification'] != null) {
            queryParameters['initiated_identification'] = requestParameters['initiatedIdentification'];
        }

        if (requestParameters['investmentTotalFulfilledMax'] != null) {
            queryParameters['investment_total_fulfilled_max'] = requestParameters['investmentTotalFulfilledMax'];
        }

        if (requestParameters['investmentTotalFulfilledMin'] != null) {
            queryParameters['investment_total_fulfilled_min'] = requestParameters['investmentTotalFulfilledMin'];
        }

        if (requestParameters['investmentTotalMax'] != null) {
            queryParameters['investment_total_max'] = requestParameters['investmentTotalMax'];
        }

        if (requestParameters['investmentTotalMin'] != null) {
            queryParameters['investment_total_min'] = requestParameters['investmentTotalMin'];
        }

        if (requestParameters['investorId'] != null) {
            queryParameters['investor_id'] = requestParameters['investorId'];
        }

        if (requestParameters['investorName'] != null) {
            queryParameters['investor_name'] = requestParameters['investorName'];
        }

        if (requestParameters['lastSettledPayoutAmountMax'] != null) {
            queryParameters['last_settled_payout_amount_max'] = requestParameters['lastSettledPayoutAmountMax'];
        }

        if (requestParameters['lastSettledPayoutAmountMin'] != null) {
            queryParameters['last_settled_payout_amount_min'] = requestParameters['lastSettledPayoutAmountMin'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['maxAmountMax'] != null) {
            queryParameters['max_amount_max'] = requestParameters['maxAmountMax'];
        }

        if (requestParameters['maxAmountMin'] != null) {
            queryParameters['max_amount_min'] = requestParameters['maxAmountMin'];
        }

        if (requestParameters['nextScheduledPayoutAfter'] != null) {
            queryParameters['next_scheduled_payout_after'] = (requestParameters['nextScheduledPayoutAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['nextScheduledPayoutBefore'] != null) {
            queryParameters['next_scheduled_payout_before'] = (requestParameters['nextScheduledPayoutBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['paidAtAfter'] != null) {
            queryParameters['paid_at_after'] = (requestParameters['paidAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paidAtBefore'] != null) {
            queryParameters['paid_at_before'] = (requestParameters['paidAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentCode'] != null) {
            queryParameters['payment_code'] = requestParameters['paymentCode'];
        }

        if (requestParameters['paymentDeadlineAfter'] != null) {
            queryParameters['payment_deadline_after'] = (requestParameters['paymentDeadlineAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentDeadlineBefore'] != null) {
            queryParameters['payment_deadline_before'] = (requestParameters['paymentDeadlineBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentTotalMax'] != null) {
            queryParameters['payment_total_max'] = requestParameters['paymentTotalMax'];
        }

        if (requestParameters['paymentTotalMin'] != null) {
            queryParameters['payment_total_min'] = requestParameters['paymentTotalMin'];
        }

        if (requestParameters['productId'] != null) {
            queryParameters['product_id'] = requestParameters['productId'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['remainingAmountMax'] != null) {
            queryParameters['remaining_amount_max'] = requestParameters['remainingAmountMax'];
        }

        if (requestParameters['remainingAmountMin'] != null) {
            queryParameters['remaining_amount_min'] = requestParameters['remainingAmountMin'];
        }

        if (requestParameters['search'] != null) {
            queryParameters['search'] = requestParameters['search'];
        }

        if (requestParameters['signedAtAfter'] != null) {
            queryParameters['signed_at_after'] = (requestParameters['signedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['signedAtBefore'] != null) {
            queryParameters['signed_at_before'] = (requestParameters['signedAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['status'] != null) {
            queryParameters['status'] = requestParameters['status'];
        }

        if (requestParameters['step'] != null) {
            queryParameters['step'] = requestParameters['step'];
        }

        if (requestParameters['sumSettledPayoutsMax'] != null) {
            queryParameters['sum_settled_payouts_max'] = requestParameters['sumSettledPayoutsMax'];
        }

        if (requestParameters['sumSettledPayoutsMin'] != null) {
            queryParameters['sum_settled_payouts_min'] = requestParameters['sumSettledPayoutsMin'];
        }

        if (requestParameters['surveyValid'] != null) {
            queryParameters['survey_valid'] = requestParameters['surveyValid'];
        }

        if (requestParameters['surveyWaiverType'] != null) {
            queryParameters['survey_waiver_type'] = requestParameters['surveyWaiverType'];
        }

        if (requestParameters['tokenDeliveredAtAfter'] != null) {
            queryParameters['token_delivered_at_after'] = (requestParameters['tokenDeliveredAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['tokenDeliveredAtBefore'] != null) {
            queryParameters['token_delivered_at_before'] = (requestParameters['tokenDeliveredAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['tokenTransactionHash'] != null) {
            queryParameters['token_transaction_hash'] = requestParameters['tokenTransactionHash'];
        }

        if (requestParameters['voucherId'] != null) {
            queryParameters['voucher_id'] = requestParameters['voucherId'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/dropdown/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedDropdownElementListFromJSON(jsonValue));
    }

    /**
     * Get dropdown elements with IDs and names.
     * Get dropdown elements
     */
    async investmentsDropdownList(requestParameters: InvestmentsDropdownListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedDropdownElementList> {
        const response = await this.investmentsDropdownListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Retrieve an identification. Create it if it does not exists.
     */
    async investmentsIdentificationRetrieveRaw(requestParameters: InvestmentsIdentificationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<KycInformation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsIdentificationRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/{id}/identification/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => KycInformationFromJSON(jsonValue));
    }

    /**
     * Retrieve an identification. Create it if it does not exists.
     */
    async investmentsIdentificationRetrieve(requestParameters: InvestmentsIdentificationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<KycInformation> {
        const response = await this.investmentsIdentificationRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Set the KYC status of an identification to \'started\'.
     */
    async investmentsIdentificationStartNotificationCreateRaw(requestParameters: InvestmentsIdentificationStartNotificationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsIdentificationStartNotificationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/{id}/identification-start-notification/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Set the KYC status of an identification to \'started\'.
     */
    async investmentsIdentificationStartNotificationCreate(requestParameters: InvestmentsIdentificationStartNotificationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsIdentificationStartNotificationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * List Investments.
     */
    async investmentsListRaw(requestParameters: InvestmentsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedInvestmentDetailsList>> {
        const queryParameters: any = {};

        if (requestParameters['acceptedAtAfter'] != null) {
            queryParameters['accepted_at_after'] = (requestParameters['acceptedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['acceptedAtBefore'] != null) {
            queryParameters['accepted_at_before'] = (requestParameters['acceptedAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['acquisitionType'] != null) {
            queryParameters['acquisition_type'] = requestParameters['acquisitionType'];
        }

        if (requestParameters['addWalletLater'] != null) {
            queryParameters['add_wallet_later'] = requestParameters['addWalletLater'];
        }

        if (requestParameters['campaign'] != null) {
            queryParameters['campaign'] = requestParameters['campaign'];
        }

        if (requestParameters['cancellationDateAfter'] != null) {
            queryParameters['cancellation_date_after'] = (requestParameters['cancellationDateAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['cancellationDateBefore'] != null) {
            queryParameters['cancellation_date_before'] = (requestParameters['cancellationDateBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['cancellationReason'] != null) {
            queryParameters['cancellation_reason'] = requestParameters['cancellationReason'];
        }

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['distributionPlatformDisplayName'] != null) {
            queryParameters['distribution_platform_display_name'] = requestParameters['distributionPlatformDisplayName'];
        }

        if (requestParameters['distributionPlatformId'] != null) {
            queryParameters['distribution_platform_id'] = requestParameters['distributionPlatformId'];
        }

        if (requestParameters['exemptionOrderDateAfter'] != null) {
            queryParameters['exemption_order_date_after'] = (requestParameters['exemptionOrderDateAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['exemptionOrderDateBefore'] != null) {
            queryParameters['exemption_order_date_before'] = (requestParameters['exemptionOrderDateBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['frontendStatus'] != null) {
            queryParameters['frontend_status'] = requestParameters['frontendStatus'];
        }

        if (requestParameters['fulfilledAmountMax'] != null) {
            queryParameters['fulfilled_amount_max'] = requestParameters['fulfilledAmountMax'];
        }

        if (requestParameters['fulfilledAmountMin'] != null) {
            queryParameters['fulfilled_amount_min'] = requestParameters['fulfilledAmountMin'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['initiatedIdentification'] != null) {
            queryParameters['initiated_identification'] = requestParameters['initiatedIdentification'];
        }

        if (requestParameters['investmentTotalFulfilledMax'] != null) {
            queryParameters['investment_total_fulfilled_max'] = requestParameters['investmentTotalFulfilledMax'];
        }

        if (requestParameters['investmentTotalFulfilledMin'] != null) {
            queryParameters['investment_total_fulfilled_min'] = requestParameters['investmentTotalFulfilledMin'];
        }

        if (requestParameters['investmentTotalMax'] != null) {
            queryParameters['investment_total_max'] = requestParameters['investmentTotalMax'];
        }

        if (requestParameters['investmentTotalMin'] != null) {
            queryParameters['investment_total_min'] = requestParameters['investmentTotalMin'];
        }

        if (requestParameters['investorId'] != null) {
            queryParameters['investor_id'] = requestParameters['investorId'];
        }

        if (requestParameters['investorName'] != null) {
            queryParameters['investor_name'] = requestParameters['investorName'];
        }

        if (requestParameters['lastSettledPayoutAmountMax'] != null) {
            queryParameters['last_settled_payout_amount_max'] = requestParameters['lastSettledPayoutAmountMax'];
        }

        if (requestParameters['lastSettledPayoutAmountMin'] != null) {
            queryParameters['last_settled_payout_amount_min'] = requestParameters['lastSettledPayoutAmountMin'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['maxAmountMax'] != null) {
            queryParameters['max_amount_max'] = requestParameters['maxAmountMax'];
        }

        if (requestParameters['maxAmountMin'] != null) {
            queryParameters['max_amount_min'] = requestParameters['maxAmountMin'];
        }

        if (requestParameters['nextScheduledPayoutAfter'] != null) {
            queryParameters['next_scheduled_payout_after'] = (requestParameters['nextScheduledPayoutAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['nextScheduledPayoutBefore'] != null) {
            queryParameters['next_scheduled_payout_before'] = (requestParameters['nextScheduledPayoutBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['paidAtAfter'] != null) {
            queryParameters['paid_at_after'] = (requestParameters['paidAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paidAtBefore'] != null) {
            queryParameters['paid_at_before'] = (requestParameters['paidAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentCode'] != null) {
            queryParameters['payment_code'] = requestParameters['paymentCode'];
        }

        if (requestParameters['paymentDeadlineAfter'] != null) {
            queryParameters['payment_deadline_after'] = (requestParameters['paymentDeadlineAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentDeadlineBefore'] != null) {
            queryParameters['payment_deadline_before'] = (requestParameters['paymentDeadlineBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentTotalMax'] != null) {
            queryParameters['payment_total_max'] = requestParameters['paymentTotalMax'];
        }

        if (requestParameters['paymentTotalMin'] != null) {
            queryParameters['payment_total_min'] = requestParameters['paymentTotalMin'];
        }

        if (requestParameters['productId'] != null) {
            queryParameters['product_id'] = requestParameters['productId'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['remainingAmountMax'] != null) {
            queryParameters['remaining_amount_max'] = requestParameters['remainingAmountMax'];
        }

        if (requestParameters['remainingAmountMin'] != null) {
            queryParameters['remaining_amount_min'] = requestParameters['remainingAmountMin'];
        }

        if (requestParameters['search'] != null) {
            queryParameters['search'] = requestParameters['search'];
        }

        if (requestParameters['signedAtAfter'] != null) {
            queryParameters['signed_at_after'] = (requestParameters['signedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['signedAtBefore'] != null) {
            queryParameters['signed_at_before'] = (requestParameters['signedAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['status'] != null) {
            queryParameters['status'] = requestParameters['status'];
        }

        if (requestParameters['step'] != null) {
            queryParameters['step'] = requestParameters['step'];
        }

        if (requestParameters['sumSettledPayoutsMax'] != null) {
            queryParameters['sum_settled_payouts_max'] = requestParameters['sumSettledPayoutsMax'];
        }

        if (requestParameters['sumSettledPayoutsMin'] != null) {
            queryParameters['sum_settled_payouts_min'] = requestParameters['sumSettledPayoutsMin'];
        }

        if (requestParameters['surveyValid'] != null) {
            queryParameters['survey_valid'] = requestParameters['surveyValid'];
        }

        if (requestParameters['surveyWaiverType'] != null) {
            queryParameters['survey_waiver_type'] = requestParameters['surveyWaiverType'];
        }

        if (requestParameters['tokenDeliveredAtAfter'] != null) {
            queryParameters['token_delivered_at_after'] = (requestParameters['tokenDeliveredAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['tokenDeliveredAtBefore'] != null) {
            queryParameters['token_delivered_at_before'] = (requestParameters['tokenDeliveredAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['tokenTransactionHash'] != null) {
            queryParameters['token_transaction_hash'] = requestParameters['tokenTransactionHash'];
        }

        if (requestParameters['voucherId'] != null) {
            queryParameters['voucher_id'] = requestParameters['voucherId'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedInvestmentDetailsListFromJSON(jsonValue));
    }

    /**
     * List Investments.
     */
    async investmentsList(requestParameters: InvestmentsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedInvestmentDetailsList> {
        const response = await this.investmentsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * If before setting up a wallet, an investor decides to choose another wallet type, then this endpoint needs to be called to reset the `add_wallet_later` field.  If a wallet was already attached but the trustline / signing flow has not yet succeeded (no SUCCEEDED ProductWalletAssignment), the wallet is also detached and the in-progress PWA is dropped so the wizard recomputes back to SELECT_WALLET.
     */
    async investmentsResetWalletSelectionCreateRaw(requestParameters: InvestmentsResetWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsResetWalletSelectionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/{id}/reset-wallet-selection/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * If before setting up a wallet, an investor decides to choose another wallet type, then this endpoint needs to be called to reset the `add_wallet_later` field.  If a wallet was already attached but the trustline / signing flow has not yet succeeded (no SUCCEEDED ProductWalletAssignment), the wallet is also detached and the in-progress PWA is dropped so the wizard recomputes back to SELECT_WALLET.
     */
    async investmentsResetWalletSelectionCreate(requestParameters: InvestmentsResetWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsResetWalletSelectionCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Retrieve an Investment.
     */
    async investmentsRetrieveRaw(requestParameters: InvestmentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestmentDetails>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestmentDetailsFromJSON(jsonValue));
    }

    /**
     * Retrieve an Investment.
     */
    async investmentsRetrieve(requestParameters: InvestmentsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestmentDetails> {
        const response = await this.investmentsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async investmentsValuesRetrieveRaw(requestParameters: InvestmentsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<string>>> {
        if (requestParameters['field'] == null) {
            throw new runtime.RequiredError(
                'field',
                'Required parameter "field" was null or undefined when calling investmentsValuesRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['acceptedAtAfter'] != null) {
            queryParameters['accepted_at_after'] = (requestParameters['acceptedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['acceptedAtBefore'] != null) {
            queryParameters['accepted_at_before'] = (requestParameters['acceptedAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['acquisitionType'] != null) {
            queryParameters['acquisition_type'] = requestParameters['acquisitionType'];
        }

        if (requestParameters['addWalletLater'] != null) {
            queryParameters['add_wallet_later'] = requestParameters['addWalletLater'];
        }

        if (requestParameters['campaign'] != null) {
            queryParameters['campaign'] = requestParameters['campaign'];
        }

        if (requestParameters['cancellationDateAfter'] != null) {
            queryParameters['cancellation_date_after'] = (requestParameters['cancellationDateAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['cancellationDateBefore'] != null) {
            queryParameters['cancellation_date_before'] = (requestParameters['cancellationDateBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['cancellationReason'] != null) {
            queryParameters['cancellation_reason'] = requestParameters['cancellationReason'];
        }

        if (requestParameters['createdAtAfter'] != null) {
            queryParameters['created_at_after'] = (requestParameters['createdAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['createdAtBefore'] != null) {
            queryParameters['created_at_before'] = (requestParameters['createdAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['distributionPlatformDisplayName'] != null) {
            queryParameters['distribution_platform_display_name'] = requestParameters['distributionPlatformDisplayName'];
        }

        if (requestParameters['distributionPlatformId'] != null) {
            queryParameters['distribution_platform_id'] = requestParameters['distributionPlatformId'];
        }

        if (requestParameters['exemptionOrderDateAfter'] != null) {
            queryParameters['exemption_order_date_after'] = (requestParameters['exemptionOrderDateAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['exemptionOrderDateBefore'] != null) {
            queryParameters['exemption_order_date_before'] = (requestParameters['exemptionOrderDateBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['field'] != null) {
            queryParameters['field'] = requestParameters['field'];
        }

        if (requestParameters['frontendStatus'] != null) {
            queryParameters['frontend_status'] = requestParameters['frontendStatus'];
        }

        if (requestParameters['fulfilledAmountMax'] != null) {
            queryParameters['fulfilled_amount_max'] = requestParameters['fulfilledAmountMax'];
        }

        if (requestParameters['fulfilledAmountMin'] != null) {
            queryParameters['fulfilled_amount_min'] = requestParameters['fulfilledAmountMin'];
        }

        if (requestParameters['icontains'] != null) {
            queryParameters['icontains'] = requestParameters['icontains'];
        }

        if (requestParameters['id'] != null) {
            queryParameters['id'] = requestParameters['id'];
        }

        if (requestParameters['initiatedIdentification'] != null) {
            queryParameters['initiated_identification'] = requestParameters['initiatedIdentification'];
        }

        if (requestParameters['investmentTotalFulfilledMax'] != null) {
            queryParameters['investment_total_fulfilled_max'] = requestParameters['investmentTotalFulfilledMax'];
        }

        if (requestParameters['investmentTotalFulfilledMin'] != null) {
            queryParameters['investment_total_fulfilled_min'] = requestParameters['investmentTotalFulfilledMin'];
        }

        if (requestParameters['investmentTotalMax'] != null) {
            queryParameters['investment_total_max'] = requestParameters['investmentTotalMax'];
        }

        if (requestParameters['investmentTotalMin'] != null) {
            queryParameters['investment_total_min'] = requestParameters['investmentTotalMin'];
        }

        if (requestParameters['investorId'] != null) {
            queryParameters['investor_id'] = requestParameters['investorId'];
        }

        if (requestParameters['investorName'] != null) {
            queryParameters['investor_name'] = requestParameters['investorName'];
        }

        if (requestParameters['lastSettledPayoutAmountMax'] != null) {
            queryParameters['last_settled_payout_amount_max'] = requestParameters['lastSettledPayoutAmountMax'];
        }

        if (requestParameters['lastSettledPayoutAmountMin'] != null) {
            queryParameters['last_settled_payout_amount_min'] = requestParameters['lastSettledPayoutAmountMin'];
        }

        if (requestParameters['maxAmountMax'] != null) {
            queryParameters['max_amount_max'] = requestParameters['maxAmountMax'];
        }

        if (requestParameters['maxAmountMin'] != null) {
            queryParameters['max_amount_min'] = requestParameters['maxAmountMin'];
        }

        if (requestParameters['nextScheduledPayoutAfter'] != null) {
            queryParameters['next_scheduled_payout_after'] = (requestParameters['nextScheduledPayoutAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['nextScheduledPayoutBefore'] != null) {
            queryParameters['next_scheduled_payout_before'] = (requestParameters['nextScheduledPayoutBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['paidAtAfter'] != null) {
            queryParameters['paid_at_after'] = (requestParameters['paidAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paidAtBefore'] != null) {
            queryParameters['paid_at_before'] = (requestParameters['paidAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentCode'] != null) {
            queryParameters['payment_code'] = requestParameters['paymentCode'];
        }

        if (requestParameters['paymentDeadlineAfter'] != null) {
            queryParameters['payment_deadline_after'] = (requestParameters['paymentDeadlineAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentDeadlineBefore'] != null) {
            queryParameters['payment_deadline_before'] = (requestParameters['paymentDeadlineBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['paymentTotalMax'] != null) {
            queryParameters['payment_total_max'] = requestParameters['paymentTotalMax'];
        }

        if (requestParameters['paymentTotalMin'] != null) {
            queryParameters['payment_total_min'] = requestParameters['paymentTotalMin'];
        }

        if (requestParameters['productId'] != null) {
            queryParameters['product_id'] = requestParameters['productId'];
        }

        if (requestParameters['productName'] != null) {
            queryParameters['product_name'] = requestParameters['productName'];
        }

        if (requestParameters['remainingAmountMax'] != null) {
            queryParameters['remaining_amount_max'] = requestParameters['remainingAmountMax'];
        }

        if (requestParameters['remainingAmountMin'] != null) {
            queryParameters['remaining_amount_min'] = requestParameters['remainingAmountMin'];
        }

        if (requestParameters['search'] != null) {
            queryParameters['search'] = requestParameters['search'];
        }

        if (requestParameters['signedAtAfter'] != null) {
            queryParameters['signed_at_after'] = (requestParameters['signedAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['signedAtBefore'] != null) {
            queryParameters['signed_at_before'] = (requestParameters['signedAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['status'] != null) {
            queryParameters['status'] = requestParameters['status'];
        }

        if (requestParameters['step'] != null) {
            queryParameters['step'] = requestParameters['step'];
        }

        if (requestParameters['sumSettledPayoutsMax'] != null) {
            queryParameters['sum_settled_payouts_max'] = requestParameters['sumSettledPayoutsMax'];
        }

        if (requestParameters['sumSettledPayoutsMin'] != null) {
            queryParameters['sum_settled_payouts_min'] = requestParameters['sumSettledPayoutsMin'];
        }

        if (requestParameters['surveyValid'] != null) {
            queryParameters['survey_valid'] = requestParameters['surveyValid'];
        }

        if (requestParameters['surveyWaiverType'] != null) {
            queryParameters['survey_waiver_type'] = requestParameters['surveyWaiverType'];
        }

        if (requestParameters['tokenDeliveredAtAfter'] != null) {
            queryParameters['token_delivered_at_after'] = (requestParameters['tokenDeliveredAtAfter'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['tokenDeliveredAtBefore'] != null) {
            queryParameters['token_delivered_at_before'] = (requestParameters['tokenDeliveredAtBefore'] as any).toISOString().substring(0,10);
        }

        if (requestParameters['tokenTransactionHash'] != null) {
            queryParameters['token_transaction_hash'] = requestParameters['tokenTransactionHash'];
        }

        if (requestParameters['voucherId'] != null) {
            queryParameters['voucher_id'] = requestParameters['voucherId'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/investments/values/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Get distinct string values for a column - no pagination, capped at 50 elements.
     * Get the names one column can have
     */
    async investmentsValuesRetrieve(requestParameters: InvestmentsValuesRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<string>> {
        const response = await this.investmentsValuesRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsWalletAssociationCreateRaw(requestParameters: InvestmentsWalletAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsWalletAssociationCreate().'
            );
        }

        if (requestParameters['investmentWalletAssociationRequest'] == null) {
            throw new runtime.RequiredError(
                'investmentWalletAssociationRequest',
                'Required parameter "investmentWalletAssociationRequest" was null or undefined when calling investmentsWalletAssociationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/investments/{id}/wallet-association/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestmentWalletAssociationRequestToJSON(requestParameters['investmentWalletAssociationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async investmentsWalletAssociationCreate(requestParameters: InvestmentsWalletAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsWalletAssociationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Select a wallet type without creating a wallet immediately. Used for mobile wallet onboarding.
     */
    async investmentsWalletSelectionCreateRaw(requestParameters: InvestmentsWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling investmentsWalletSelectionCreate().'
            );
        }

        if (requestParameters['investmentWalletSelectionRequest'] == null) {
            throw new runtime.RequiredError(
                'investmentWalletSelectionRequest',
                'Required parameter "investmentWalletSelectionRequest" was null or undefined when calling investmentsWalletSelectionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/investments/{id}/wallet-selection/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestmentWalletSelectionRequestToJSON(requestParameters['investmentWalletSelectionRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Select a wallet type without creating a wallet immediately. Used for mobile wallet onboarding.
     */
    async investmentsWalletSelectionCreate(requestParameters: InvestmentsWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.investmentsWalletSelectionCreateRaw(requestParameters, initOverrides);
    }

}

/**
 * @export
 */
export const InvestmentsDropdownListAcquisitionTypeEnum = {
    HANDOVER: 'HANDOVER',
    ISSUANCE: 'ISSUANCE'
} as const;
export type InvestmentsDropdownListAcquisitionTypeEnum = typeof InvestmentsDropdownListAcquisitionTypeEnum[keyof typeof InvestmentsDropdownListAcquisitionTypeEnum];
/**
 * @export
 */
export const InvestmentsDropdownListAddWalletLaterEnum = {
    CASHLINK: 'CASHLINK',
    GENERIC: 'GENERIC',
    MOBILE: 'MOBILE',
    TANGANY: 'TANGANY'
} as const;
export type InvestmentsDropdownListAddWalletLaterEnum = typeof InvestmentsDropdownListAddWalletLaterEnum[keyof typeof InvestmentsDropdownListAddWalletLaterEnum];
/**
 * @export
 */
export const InvestmentsDropdownListCancellationReasonEnum = {
    ABORTION: 'ABORTION',
    CANCELLATION: 'CANCELLATION',
    DISTRIBUTION_INFORMATION_UPDATED: 'DISTRIBUTION_INFORMATION_UPDATED',
    KYC_INSUFFICIENT: 'KYC_INSUFFICIENT',
    PAYMENT_MISSING: 'PAYMENT_MISSING',
    REJECTION: 'REJECTION',
    REJECTION_ECSP: 'REJECTION_ECSP'
} as const;
export type InvestmentsDropdownListCancellationReasonEnum = typeof InvestmentsDropdownListCancellationReasonEnum[keyof typeof InvestmentsDropdownListCancellationReasonEnum];
/**
 * @export
 */
export const InvestmentsDropdownListFieldEnum = {
    PRODUCT_NAME: 'product_name',
    TOKEN_TRANSACTION_HASH: 'token_transaction_hash'
} as const;
export type InvestmentsDropdownListFieldEnum = typeof InvestmentsDropdownListFieldEnum[keyof typeof InvestmentsDropdownListFieldEnum];
/**
 * @export
 */
export const InvestmentsDropdownListFrontendStatusEnum = {
    ABORTED: 'Aborted',
    ACCEPTANCE_MISSING: 'AcceptanceMissing',
    ADDITIONAL_INFORMATION_PENDING: 'AdditionalInformationPending',
    CANCELED_KYC_INSUFFICIENT: 'CanceledKycInsufficient',
    CANCELED_PAYMENT_MISSING: 'CanceledPaymentMissing',
    CANCELLATION: 'Cancellation',
    FINISHED: 'Finished',
    HANDOVER: 'Handover',
    IDENTIFICATION_COMPLIANCE_PENDING: 'IdentificationCompliancePending',
    IDENTIFICATION_PENDING: 'IdentificationPending',
    PAYMENT_PENDING: 'PaymentPending',
    REJECTED: 'Rejected',
    SECURITIES_TRANSFERRED: 'SecuritiesTransferred',
    SECURITY_TRANSFER_MISSING: 'SecurityTransferMissing',
    SUCCESS_FULLY_TRANSFERED: 'SuccessFullyTransfered',
    SUCCESS_PARTIALLY_TRANSFERED: 'SuccessPartiallyTransfered',
    SUCCESS_REDEEMED: 'SuccessRedeemed',
    TAX_INFORMATION_MISSING: 'TaxInformationMissing',
    TRANSACTION_FAILED: 'TransactionFailed',
    TRANSACTION_PENDING: 'TransactionPending',
    TRANSACTION_SUCCEEDED: 'TransactionSucceeded',
    WALLET_SETUP_PENDING: 'WalletSetupPending'
} as const;
export type InvestmentsDropdownListFrontendStatusEnum = typeof InvestmentsDropdownListFrontendStatusEnum[keyof typeof InvestmentsDropdownListFrontendStatusEnum];
/**
 * @export
 */
export const InvestmentsDropdownListOrderingEnum = {
    ACCEPTED_AT_DESC: '-accepted_at',
    ACCEPTED_AT_ASC: 'accepted_at',
    ACQUISITION_TYPE_DESC: '-acquisition_type',
    ACQUISITION_TYPE_ASC: 'acquisition_type',
    ADD_WALLET_LATER_DESC: '-add_wallet_later',
    ADD_WALLET_LATER_ASC: 'add_wallet_later',
    CANCELLATION_DATE_DESC: '-cancellation_date',
    CANCELLATION_DATE_ASC: 'cancellation_date',
    CANCELLATION_REASON_DESC: '-cancellation_reason',
    CANCELLATION_REASON_ASC: 'cancellation_reason',
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    EXEMPTION_ORDER_DATE_DESC: '-exemption_order_date',
    EXEMPTION_ORDER_DATE_ASC: 'exemption_order_date',
    FRONTEND_STATUS_DESC: '-frontend_status',
    FRONTEND_STATUS_ASC: 'frontend_status',
    FULFILLED_AMOUNT_DESC: '-fulfilled_amount',
    FULFILLED_AMOUNT_ASC: 'fulfilled_amount',
    ID_DESC: '-id',
    ID_ASC: 'id',
    INITIATED_IDENTIFICATION_DESC: '-initiated_identification',
    INITIATED_IDENTIFICATION_ASC: 'initiated_identification',
    INVESTMENT_TOTAL_DESC: '-investment_total',
    INVESTMENT_TOTAL_ASC: 'investment_total',
    INVESTMENT_TOTAL_FULFILLED_DESC: '-investment_total_fulfilled',
    INVESTMENT_TOTAL_FULFILLED_ASC: 'investment_total_fulfilled',
    LAST_SETTLED_PAYOUT_DESC: '-last_settled_payout',
    LAST_SETTLED_PAYOUT_ASC: 'last_settled_payout',
    MAX_AMOUNT_DESC: '-max_amount',
    MAX_AMOUNT_ASC: 'max_amount',
    NEXT_SCHEDULED_PAYOUT_DESC: '-next_scheduled_payout',
    NEXT_SCHEDULED_PAYOUT_ASC: 'next_scheduled_payout',
    PAID_AT_DESC: '-paid_at',
    PAID_AT_ASC: 'paid_at',
    PAYMENT_CODE_DESC: '-payment_code',
    PAYMENT_CODE_ASC: 'payment_code',
    PAYMENT_DEADLINE_DESC: '-payment_deadline',
    PAYMENT_DEADLINE_ASC: 'payment_deadline',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    REMAINING_AMOUNT_DESC: '-remaining_amount',
    REMAINING_AMOUNT_ASC: 'remaining_amount',
    SIGNED_AT_DESC: '-signed_at',
    SIGNED_AT_ASC: 'signed_at',
    STEP_DESC: '-step',
    STEP_ASC: 'step',
    SUM_SETTLED_PAYOUTS_DESC: '-sum_settled_payouts',
    SUM_SETTLED_PAYOUTS_ASC: 'sum_settled_payouts',
    SURVEY_VALID_DESC: '-survey_valid',
    SURVEY_VALID_ASC: 'survey_valid',
    SURVEY_WAIVER_TYPE_DESC: '-survey_waiver_type',
    SURVEY_WAIVER_TYPE_ASC: 'survey_waiver_type',
    TOKEN_DELIVERED_AT_DESC: '-token_delivered_at',
    TOKEN_DELIVERED_AT_ASC: 'token_delivered_at',
    TOKEN_TRANSACTION_HASH_DESC: '-token_transaction_hash',
    TOKEN_TRANSACTION_HASH_ASC: 'token_transaction_hash'
} as const;
export type InvestmentsDropdownListOrderingEnum = typeof InvestmentsDropdownListOrderingEnum[keyof typeof InvestmentsDropdownListOrderingEnum];
/**
 * @export
 */
export const InvestmentsDropdownListStatusEnum = {
    ACCEPTABLE: 'acceptable',
    ALL: 'all',
    FINISHED: 'finished',
    NOT_DELETED: 'not-deleted',
    OPEN: 'open',
    PAID: 'paid',
    PAYMENT_OUTSTANDING: 'payment-outstanding',
    SECURITIES_OUTSTANDING: 'securities-outstanding'
} as const;
export type InvestmentsDropdownListStatusEnum = typeof InvestmentsDropdownListStatusEnum[keyof typeof InvestmentsDropdownListStatusEnum];
/**
 * @export
 */
export const InvestmentsDropdownListStepEnum = {
    ACCOUNT_SETUP: 'ACCOUNT_SETUP',
    ADDITIONAL_INFORMATION: 'ADDITIONAL_INFORMATION',
    BENEFICIARY: 'BENEFICIARY',
    CANCELED: 'CANCELED',
    CONFIRM_WALLET: 'CONFIRM_WALLET',
    FINAL: 'FINAL',
    IDENTIFICATION: 'IDENTIFICATION',
    IDENTIFICATION_FAILED: 'IDENTIFICATION_FAILED',
    IDENTIFICATION_REJECTED: 'IDENTIFICATION_REJECTED',
    INVESTMENT_AMOUNT: 'INVESTMENT_AMOUNT',
    OFFER_CREATION: 'OFFER_CREATION',
    PAYMENT: 'PAYMENT',
    PREPARE_ASSET: 'PREPARE_ASSET',
    QUESTIONNAIRE: 'QUESTIONNAIRE',
    RESUME_IDENTIFICATION: 'RESUME_IDENTIFICATION',
    SELECT_WALLET: 'SELECT_WALLET',
    SETUP_MOBILE_WALLET: 'SETUP_MOBILE_WALLET',
    TAX_INFORMATION: 'TAX_INFORMATION',
    UNCONFIRMED: 'UNCONFIRMED',
    VIB: 'VIB',
    WAITING_ACCEPTANCE: 'WAITING_ACCEPTANCE',
    WAITING_COMPLIANCE: 'WAITING_COMPLIANCE',
    WAITING_KYC: 'WAITING_KYC'
} as const;
export type InvestmentsDropdownListStepEnum = typeof InvestmentsDropdownListStepEnum[keyof typeof InvestmentsDropdownListStepEnum];
/**
 * @export
 */
export const InvestmentsDropdownListSurveyWaiverTypeEnum = {
    NO_ANSWERS: 'NO_ANSWERS',
    UNSUITABLE: 'UNSUITABLE'
} as const;
export type InvestmentsDropdownListSurveyWaiverTypeEnum = typeof InvestmentsDropdownListSurveyWaiverTypeEnum[keyof typeof InvestmentsDropdownListSurveyWaiverTypeEnum];
/**
 * @export
 */
export const InvestmentsListAcquisitionTypeEnum = {
    HANDOVER: 'HANDOVER',
    ISSUANCE: 'ISSUANCE'
} as const;
export type InvestmentsListAcquisitionTypeEnum = typeof InvestmentsListAcquisitionTypeEnum[keyof typeof InvestmentsListAcquisitionTypeEnum];
/**
 * @export
 */
export const InvestmentsListAddWalletLaterEnum = {
    CASHLINK: 'CASHLINK',
    GENERIC: 'GENERIC',
    MOBILE: 'MOBILE',
    TANGANY: 'TANGANY'
} as const;
export type InvestmentsListAddWalletLaterEnum = typeof InvestmentsListAddWalletLaterEnum[keyof typeof InvestmentsListAddWalletLaterEnum];
/**
 * @export
 */
export const InvestmentsListCancellationReasonEnum = {
    ABORTION: 'ABORTION',
    CANCELLATION: 'CANCELLATION',
    DISTRIBUTION_INFORMATION_UPDATED: 'DISTRIBUTION_INFORMATION_UPDATED',
    KYC_INSUFFICIENT: 'KYC_INSUFFICIENT',
    PAYMENT_MISSING: 'PAYMENT_MISSING',
    REJECTION: 'REJECTION',
    REJECTION_ECSP: 'REJECTION_ECSP'
} as const;
export type InvestmentsListCancellationReasonEnum = typeof InvestmentsListCancellationReasonEnum[keyof typeof InvestmentsListCancellationReasonEnum];
/**
 * @export
 */
export const InvestmentsListFrontendStatusEnum = {
    ABORTED: 'Aborted',
    ACCEPTANCE_MISSING: 'AcceptanceMissing',
    ADDITIONAL_INFORMATION_PENDING: 'AdditionalInformationPending',
    CANCELED_KYC_INSUFFICIENT: 'CanceledKycInsufficient',
    CANCELED_PAYMENT_MISSING: 'CanceledPaymentMissing',
    CANCELLATION: 'Cancellation',
    FINISHED: 'Finished',
    HANDOVER: 'Handover',
    IDENTIFICATION_COMPLIANCE_PENDING: 'IdentificationCompliancePending',
    IDENTIFICATION_PENDING: 'IdentificationPending',
    PAYMENT_PENDING: 'PaymentPending',
    REJECTED: 'Rejected',
    SECURITIES_TRANSFERRED: 'SecuritiesTransferred',
    SECURITY_TRANSFER_MISSING: 'SecurityTransferMissing',
    SUCCESS_FULLY_TRANSFERED: 'SuccessFullyTransfered',
    SUCCESS_PARTIALLY_TRANSFERED: 'SuccessPartiallyTransfered',
    SUCCESS_REDEEMED: 'SuccessRedeemed',
    TAX_INFORMATION_MISSING: 'TaxInformationMissing',
    TRANSACTION_FAILED: 'TransactionFailed',
    TRANSACTION_PENDING: 'TransactionPending',
    TRANSACTION_SUCCEEDED: 'TransactionSucceeded',
    WALLET_SETUP_PENDING: 'WalletSetupPending'
} as const;
export type InvestmentsListFrontendStatusEnum = typeof InvestmentsListFrontendStatusEnum[keyof typeof InvestmentsListFrontendStatusEnum];
/**
 * @export
 */
export const InvestmentsListOrderingEnum = {
    ACCEPTED_AT_DESC: '-accepted_at',
    ACCEPTED_AT_ASC: 'accepted_at',
    ACQUISITION_TYPE_DESC: '-acquisition_type',
    ACQUISITION_TYPE_ASC: 'acquisition_type',
    ADD_WALLET_LATER_DESC: '-add_wallet_later',
    ADD_WALLET_LATER_ASC: 'add_wallet_later',
    CANCELLATION_DATE_DESC: '-cancellation_date',
    CANCELLATION_DATE_ASC: 'cancellation_date',
    CANCELLATION_REASON_DESC: '-cancellation_reason',
    CANCELLATION_REASON_ASC: 'cancellation_reason',
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    EXEMPTION_ORDER_DATE_DESC: '-exemption_order_date',
    EXEMPTION_ORDER_DATE_ASC: 'exemption_order_date',
    FRONTEND_STATUS_DESC: '-frontend_status',
    FRONTEND_STATUS_ASC: 'frontend_status',
    FULFILLED_AMOUNT_DESC: '-fulfilled_amount',
    FULFILLED_AMOUNT_ASC: 'fulfilled_amount',
    ID_DESC: '-id',
    ID_ASC: 'id',
    INITIATED_IDENTIFICATION_DESC: '-initiated_identification',
    INITIATED_IDENTIFICATION_ASC: 'initiated_identification',
    INVESTMENT_TOTAL_DESC: '-investment_total',
    INVESTMENT_TOTAL_ASC: 'investment_total',
    INVESTMENT_TOTAL_FULFILLED_DESC: '-investment_total_fulfilled',
    INVESTMENT_TOTAL_FULFILLED_ASC: 'investment_total_fulfilled',
    LAST_SETTLED_PAYOUT_DESC: '-last_settled_payout',
    LAST_SETTLED_PAYOUT_ASC: 'last_settled_payout',
    MAX_AMOUNT_DESC: '-max_amount',
    MAX_AMOUNT_ASC: 'max_amount',
    NEXT_SCHEDULED_PAYOUT_DESC: '-next_scheduled_payout',
    NEXT_SCHEDULED_PAYOUT_ASC: 'next_scheduled_payout',
    PAID_AT_DESC: '-paid_at',
    PAID_AT_ASC: 'paid_at',
    PAYMENT_CODE_DESC: '-payment_code',
    PAYMENT_CODE_ASC: 'payment_code',
    PAYMENT_DEADLINE_DESC: '-payment_deadline',
    PAYMENT_DEADLINE_ASC: 'payment_deadline',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    REMAINING_AMOUNT_DESC: '-remaining_amount',
    REMAINING_AMOUNT_ASC: 'remaining_amount',
    SIGNED_AT_DESC: '-signed_at',
    SIGNED_AT_ASC: 'signed_at',
    STEP_DESC: '-step',
    STEP_ASC: 'step',
    SUM_SETTLED_PAYOUTS_DESC: '-sum_settled_payouts',
    SUM_SETTLED_PAYOUTS_ASC: 'sum_settled_payouts',
    SURVEY_VALID_DESC: '-survey_valid',
    SURVEY_VALID_ASC: 'survey_valid',
    SURVEY_WAIVER_TYPE_DESC: '-survey_waiver_type',
    SURVEY_WAIVER_TYPE_ASC: 'survey_waiver_type',
    TOKEN_DELIVERED_AT_DESC: '-token_delivered_at',
    TOKEN_DELIVERED_AT_ASC: 'token_delivered_at',
    TOKEN_TRANSACTION_HASH_DESC: '-token_transaction_hash',
    TOKEN_TRANSACTION_HASH_ASC: 'token_transaction_hash'
} as const;
export type InvestmentsListOrderingEnum = typeof InvestmentsListOrderingEnum[keyof typeof InvestmentsListOrderingEnum];
/**
 * @export
 */
export const InvestmentsListStatusEnum = {
    ACCEPTABLE: 'acceptable',
    ALL: 'all',
    FINISHED: 'finished',
    NOT_DELETED: 'not-deleted',
    OPEN: 'open',
    PAID: 'paid',
    PAYMENT_OUTSTANDING: 'payment-outstanding',
    SECURITIES_OUTSTANDING: 'securities-outstanding'
} as const;
export type InvestmentsListStatusEnum = typeof InvestmentsListStatusEnum[keyof typeof InvestmentsListStatusEnum];
/**
 * @export
 */
export const InvestmentsListStepEnum = {
    ACCOUNT_SETUP: 'ACCOUNT_SETUP',
    ADDITIONAL_INFORMATION: 'ADDITIONAL_INFORMATION',
    BENEFICIARY: 'BENEFICIARY',
    CANCELED: 'CANCELED',
    CONFIRM_WALLET: 'CONFIRM_WALLET',
    FINAL: 'FINAL',
    IDENTIFICATION: 'IDENTIFICATION',
    IDENTIFICATION_FAILED: 'IDENTIFICATION_FAILED',
    IDENTIFICATION_REJECTED: 'IDENTIFICATION_REJECTED',
    INVESTMENT_AMOUNT: 'INVESTMENT_AMOUNT',
    OFFER_CREATION: 'OFFER_CREATION',
    PAYMENT: 'PAYMENT',
    PREPARE_ASSET: 'PREPARE_ASSET',
    QUESTIONNAIRE: 'QUESTIONNAIRE',
    RESUME_IDENTIFICATION: 'RESUME_IDENTIFICATION',
    SELECT_WALLET: 'SELECT_WALLET',
    SETUP_MOBILE_WALLET: 'SETUP_MOBILE_WALLET',
    TAX_INFORMATION: 'TAX_INFORMATION',
    UNCONFIRMED: 'UNCONFIRMED',
    VIB: 'VIB',
    WAITING_ACCEPTANCE: 'WAITING_ACCEPTANCE',
    WAITING_COMPLIANCE: 'WAITING_COMPLIANCE',
    WAITING_KYC: 'WAITING_KYC'
} as const;
export type InvestmentsListStepEnum = typeof InvestmentsListStepEnum[keyof typeof InvestmentsListStepEnum];
/**
 * @export
 */
export const InvestmentsListSurveyWaiverTypeEnum = {
    NO_ANSWERS: 'NO_ANSWERS',
    UNSUITABLE: 'UNSUITABLE'
} as const;
export type InvestmentsListSurveyWaiverTypeEnum = typeof InvestmentsListSurveyWaiverTypeEnum[keyof typeof InvestmentsListSurveyWaiverTypeEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveFieldEnum = {
    PRODUCT_NAME: 'product_name',
    TOKEN_TRANSACTION_HASH: 'token_transaction_hash'
} as const;
export type InvestmentsValuesRetrieveFieldEnum = typeof InvestmentsValuesRetrieveFieldEnum[keyof typeof InvestmentsValuesRetrieveFieldEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveAcquisitionTypeEnum = {
    HANDOVER: 'HANDOVER',
    ISSUANCE: 'ISSUANCE'
} as const;
export type InvestmentsValuesRetrieveAcquisitionTypeEnum = typeof InvestmentsValuesRetrieveAcquisitionTypeEnum[keyof typeof InvestmentsValuesRetrieveAcquisitionTypeEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveAddWalletLaterEnum = {
    CASHLINK: 'CASHLINK',
    GENERIC: 'GENERIC',
    MOBILE: 'MOBILE',
    TANGANY: 'TANGANY'
} as const;
export type InvestmentsValuesRetrieveAddWalletLaterEnum = typeof InvestmentsValuesRetrieveAddWalletLaterEnum[keyof typeof InvestmentsValuesRetrieveAddWalletLaterEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveCancellationReasonEnum = {
    ABORTION: 'ABORTION',
    CANCELLATION: 'CANCELLATION',
    DISTRIBUTION_INFORMATION_UPDATED: 'DISTRIBUTION_INFORMATION_UPDATED',
    KYC_INSUFFICIENT: 'KYC_INSUFFICIENT',
    PAYMENT_MISSING: 'PAYMENT_MISSING',
    REJECTION: 'REJECTION',
    REJECTION_ECSP: 'REJECTION_ECSP'
} as const;
export type InvestmentsValuesRetrieveCancellationReasonEnum = typeof InvestmentsValuesRetrieveCancellationReasonEnum[keyof typeof InvestmentsValuesRetrieveCancellationReasonEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveFrontendStatusEnum = {
    ABORTED: 'Aborted',
    ACCEPTANCE_MISSING: 'AcceptanceMissing',
    ADDITIONAL_INFORMATION_PENDING: 'AdditionalInformationPending',
    CANCELED_KYC_INSUFFICIENT: 'CanceledKycInsufficient',
    CANCELED_PAYMENT_MISSING: 'CanceledPaymentMissing',
    CANCELLATION: 'Cancellation',
    FINISHED: 'Finished',
    HANDOVER: 'Handover',
    IDENTIFICATION_COMPLIANCE_PENDING: 'IdentificationCompliancePending',
    IDENTIFICATION_PENDING: 'IdentificationPending',
    PAYMENT_PENDING: 'PaymentPending',
    REJECTED: 'Rejected',
    SECURITIES_TRANSFERRED: 'SecuritiesTransferred',
    SECURITY_TRANSFER_MISSING: 'SecurityTransferMissing',
    SUCCESS_FULLY_TRANSFERED: 'SuccessFullyTransfered',
    SUCCESS_PARTIALLY_TRANSFERED: 'SuccessPartiallyTransfered',
    SUCCESS_REDEEMED: 'SuccessRedeemed',
    TAX_INFORMATION_MISSING: 'TaxInformationMissing',
    TRANSACTION_FAILED: 'TransactionFailed',
    TRANSACTION_PENDING: 'TransactionPending',
    TRANSACTION_SUCCEEDED: 'TransactionSucceeded',
    WALLET_SETUP_PENDING: 'WalletSetupPending'
} as const;
export type InvestmentsValuesRetrieveFrontendStatusEnum = typeof InvestmentsValuesRetrieveFrontendStatusEnum[keyof typeof InvestmentsValuesRetrieveFrontendStatusEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveOrderingEnum = {
    ACCEPTED_AT_DESC: '-accepted_at',
    ACCEPTED_AT_ASC: 'accepted_at',
    ACQUISITION_TYPE_DESC: '-acquisition_type',
    ACQUISITION_TYPE_ASC: 'acquisition_type',
    ADD_WALLET_LATER_DESC: '-add_wallet_later',
    ADD_WALLET_LATER_ASC: 'add_wallet_later',
    CANCELLATION_DATE_DESC: '-cancellation_date',
    CANCELLATION_DATE_ASC: 'cancellation_date',
    CANCELLATION_REASON_DESC: '-cancellation_reason',
    CANCELLATION_REASON_ASC: 'cancellation_reason',
    CREATED_AT_DESC: '-created_at',
    CREATED_AT_ASC: 'created_at',
    EXEMPTION_ORDER_DATE_DESC: '-exemption_order_date',
    EXEMPTION_ORDER_DATE_ASC: 'exemption_order_date',
    FRONTEND_STATUS_DESC: '-frontend_status',
    FRONTEND_STATUS_ASC: 'frontend_status',
    FULFILLED_AMOUNT_DESC: '-fulfilled_amount',
    FULFILLED_AMOUNT_ASC: 'fulfilled_amount',
    ID_DESC: '-id',
    ID_ASC: 'id',
    INITIATED_IDENTIFICATION_DESC: '-initiated_identification',
    INITIATED_IDENTIFICATION_ASC: 'initiated_identification',
    INVESTMENT_TOTAL_DESC: '-investment_total',
    INVESTMENT_TOTAL_ASC: 'investment_total',
    INVESTMENT_TOTAL_FULFILLED_DESC: '-investment_total_fulfilled',
    INVESTMENT_TOTAL_FULFILLED_ASC: 'investment_total_fulfilled',
    LAST_SETTLED_PAYOUT_DESC: '-last_settled_payout',
    LAST_SETTLED_PAYOUT_ASC: 'last_settled_payout',
    MAX_AMOUNT_DESC: '-max_amount',
    MAX_AMOUNT_ASC: 'max_amount',
    NEXT_SCHEDULED_PAYOUT_DESC: '-next_scheduled_payout',
    NEXT_SCHEDULED_PAYOUT_ASC: 'next_scheduled_payout',
    PAID_AT_DESC: '-paid_at',
    PAID_AT_ASC: 'paid_at',
    PAYMENT_CODE_DESC: '-payment_code',
    PAYMENT_CODE_ASC: 'payment_code',
    PAYMENT_DEADLINE_DESC: '-payment_deadline',
    PAYMENT_DEADLINE_ASC: 'payment_deadline',
    PRODUCT_NAME_DESC: '-product_name',
    PRODUCT_NAME_ASC: 'product_name',
    REMAINING_AMOUNT_DESC: '-remaining_amount',
    REMAINING_AMOUNT_ASC: 'remaining_amount',
    SIGNED_AT_DESC: '-signed_at',
    SIGNED_AT_ASC: 'signed_at',
    STEP_DESC: '-step',
    STEP_ASC: 'step',
    SUM_SETTLED_PAYOUTS_DESC: '-sum_settled_payouts',
    SUM_SETTLED_PAYOUTS_ASC: 'sum_settled_payouts',
    SURVEY_VALID_DESC: '-survey_valid',
    SURVEY_VALID_ASC: 'survey_valid',
    SURVEY_WAIVER_TYPE_DESC: '-survey_waiver_type',
    SURVEY_WAIVER_TYPE_ASC: 'survey_waiver_type',
    TOKEN_DELIVERED_AT_DESC: '-token_delivered_at',
    TOKEN_DELIVERED_AT_ASC: 'token_delivered_at',
    TOKEN_TRANSACTION_HASH_DESC: '-token_transaction_hash',
    TOKEN_TRANSACTION_HASH_ASC: 'token_transaction_hash'
} as const;
export type InvestmentsValuesRetrieveOrderingEnum = typeof InvestmentsValuesRetrieveOrderingEnum[keyof typeof InvestmentsValuesRetrieveOrderingEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveStatusEnum = {
    ACCEPTABLE: 'acceptable',
    ALL: 'all',
    FINISHED: 'finished',
    NOT_DELETED: 'not-deleted',
    OPEN: 'open',
    PAID: 'paid',
    PAYMENT_OUTSTANDING: 'payment-outstanding',
    SECURITIES_OUTSTANDING: 'securities-outstanding'
} as const;
export type InvestmentsValuesRetrieveStatusEnum = typeof InvestmentsValuesRetrieveStatusEnum[keyof typeof InvestmentsValuesRetrieveStatusEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveStepEnum = {
    ACCOUNT_SETUP: 'ACCOUNT_SETUP',
    ADDITIONAL_INFORMATION: 'ADDITIONAL_INFORMATION',
    BENEFICIARY: 'BENEFICIARY',
    CANCELED: 'CANCELED',
    CONFIRM_WALLET: 'CONFIRM_WALLET',
    FINAL: 'FINAL',
    IDENTIFICATION: 'IDENTIFICATION',
    IDENTIFICATION_FAILED: 'IDENTIFICATION_FAILED',
    IDENTIFICATION_REJECTED: 'IDENTIFICATION_REJECTED',
    INVESTMENT_AMOUNT: 'INVESTMENT_AMOUNT',
    OFFER_CREATION: 'OFFER_CREATION',
    PAYMENT: 'PAYMENT',
    PREPARE_ASSET: 'PREPARE_ASSET',
    QUESTIONNAIRE: 'QUESTIONNAIRE',
    RESUME_IDENTIFICATION: 'RESUME_IDENTIFICATION',
    SELECT_WALLET: 'SELECT_WALLET',
    SETUP_MOBILE_WALLET: 'SETUP_MOBILE_WALLET',
    TAX_INFORMATION: 'TAX_INFORMATION',
    UNCONFIRMED: 'UNCONFIRMED',
    VIB: 'VIB',
    WAITING_ACCEPTANCE: 'WAITING_ACCEPTANCE',
    WAITING_COMPLIANCE: 'WAITING_COMPLIANCE',
    WAITING_KYC: 'WAITING_KYC'
} as const;
export type InvestmentsValuesRetrieveStepEnum = typeof InvestmentsValuesRetrieveStepEnum[keyof typeof InvestmentsValuesRetrieveStepEnum];
/**
 * @export
 */
export const InvestmentsValuesRetrieveSurveyWaiverTypeEnum = {
    NO_ANSWERS: 'NO_ANSWERS',
    UNSUITABLE: 'UNSUITABLE'
} as const;
export type InvestmentsValuesRetrieveSurveyWaiverTypeEnum = typeof InvestmentsValuesRetrieveSurveyWaiverTypeEnum[keyof typeof InvestmentsValuesRetrieveSurveyWaiverTypeEnum];
